/**
 * CloseVersionDialog — confirm + edit dialog dla skrócenia validTo na wersji.
 *
 * @see AC-10 in 2026-05-05-billing-pricing-admin-ui spec
 */
import React, { useEffect, useRef, useState } from "react";
import { closePricingComponentVersion } from "../../../../services/adminProductCatalogApi";
import type {
  PricingComponentVersion,
  PricingMutationConflictReason,
} from "../../../../types/adminBilling";
import { toIsoFromLocalDate } from "../utils/datetimeUtils";

/** Soft horizon for `validTo` warning (security LOW-3, review 2026-05-06). */
const VALID_TO_FAR_FUTURE_MS = 5 * 365 * 24 * 60 * 60 * 1000; // ~5 years

/**
 * Soft check: is the supplied date more than 5 years from now?
 * Used to warn admins about typos like 2076 vs 2026 — see security LOW-3.
 */
function isFarFuture(dateString: string): boolean {
  if (!dateString) return false;
  const ts = new Date(dateString).getTime();
  if (!Number.isFinite(ts)) return false;
  return ts > Date.now() + VALID_TO_FAR_FUTURE_MS;
}

interface CloseVersionDialogProps {
  version: PricingComponentVersion;
  brandId: string;
  onClose: () => void;
  onSuccess: (updated: PricingComponentVersion) => void;
}

export const CloseVersionDialog: React.FC<CloseVersionDialogProps> = ({
  version,
  brandId,
  onClose,
  onSuccess,
}) => {
  const [validTo, setValidTo] = useState<string>(() => {
    if (version.validTo) {
      return new Date(version.validTo).toISOString().slice(0, 16);
    }
    return "";
  });
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Auto-focus first interactive element on open (a11y)
  const firstFocusRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    firstFocusRef.current?.focus();
  }, []);

  // Escape-to-close (a11y)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSubmit = async () => {
    setError(null);
    if (!validTo) {
      setError("Podaj nowe validTo");
      return;
    }
    if (version.version === undefined) {
      setError("Brak optimistic-lock value (odśwież widok)");
      return;
    }
    setIsSubmitting(true);
    try {
      const updated = await closePricingComponentVersion(version.id, brandId, {
        validTo: toIsoFromLocalDate(validTo),
        version: version.version,
      });
      // Brief success feedback before modal closes (UX MEDIUM batch E task 3).
      setJustSaved(true);
      setTimeout(() => onSuccess(updated), 800);
    } catch (e: unknown) {
      const err = e as {
        response?: {
          status: number;
          data?: { reason?: PricingMutationConflictReason; message?: string };
        };
      };
      if (err.response?.status === 409) {
        setError(reasonMessage(err.response.data?.reason));
      } else {
        setError(err.response?.data?.message ?? "Nieoczekiwany błąd");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="close-version-dialog-title"
        className="bg-white rounded-lg max-w-md w-full mx-4"
      >
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3
            id="close-version-dialog-title"
            className="text-lg font-semibold text-neutral-900"
          >
            Skróć ważność wersji
          </h3>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-neutral-700">
            Obecne „Obowiązuje do":{" "}
            <code className="text-xs bg-neutral-100 px-1 rounded">
              {version.validTo
                ? new Date(version.validTo).toLocaleString("pl-PL")
                : "∞"}
            </code>
          </p>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Nowe „Obowiązuje do" *
            </label>
            <input
              ref={firstFocusRef}
              type="datetime-local"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              className="w-full text-sm border border-neutral-300 rounded p-1.5"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Czas polski (Europe/Warsaw)
            </p>
            {validTo && isFarFuture(validTo) && (
              <small
                data-testid="close-version-validTo-far-warning"
                className="block text-xs text-amber-600 mt-1"
              >
                Uwaga: data ważności jest oddalona o ponad 5 lat. Czy to
                celowe?
              </small>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              id="close-version-confirm"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <label htmlFor="close-version-confirm">
              Potwierdzam — operacja zmienia widoczność wersji w UI tenantów
            </label>
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || !confirmed || justSaved}
            className="px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded disabled:opacity-50"
          >
            {justSaved
              ? "Zamknięto wersję!"
              : isSubmitting
              ? "Zapisywanie..."
              : "Skróć"}
          </button>
        </div>
      </div>
    </div>
  );
};

function reasonMessage(reason?: PricingMutationConflictReason): string {
  switch (reason) {
    case "VERSION_MISMATCH":
      return "Ktoś inny zmienił wersję — odśwież widok i spróbuj ponownie";
    case "INVALID_RANGE":
      return "validTo musi być większe niż validFrom";
    default:
      return "Konflikt — operacja nieudana";
  }
}
